import isEqual from 'lodash/isEqual';
import { nullSafeGetWithZero } from './DataAccessUtils';

/**
 * Maps an active list-filter param key to the accessor path on a work order object
 * that the filter constrains. Paths intentionally point at fields that are RELIABLY
 * PRESENT on the lighter search/list projection (see workOrderSearchAndAPIAdaptor) —
 * e.g. `problemType.hierarchyName`/`supplierFacility.displayName` rather than `.id`,
 * which the list payload omits. A key that is absent here (or resolves to `undefined`
 * in the search version) is simply skipped, so an over-inclusive map only risks
 * under-detection, never a spurious re-fetch.
 */
const WO_FILTER_FIELD_ACCESSORS = {
	displayStatus: 'displayStatus',
	displayStatuses: 'displayStatus',
	status: 'status',
	statuses: 'status',
	woPriorityIds: 'woPriority.id',
	locationIds: 'location.id',
	areaHierarchyNames: 'area.hierarchyName',
	areaIdsOrNull: 'area.id',
	problemTypeIds: 'problemType.hierarchyName',
	assetIds: 'asset.id',
	assetTypeIds: 'asset.assetType.id',
	supplierFacilityIds: 'supplierFacility.displayName',
	spendCategoryIds: 'spendCategory.name',
	resolutionTypeIds: 'resolutionType.id',
	fieldTechnicianEmail: 'lastServiceCall.leadTechnicianEmail',
	isCapex: 'isCapex',
	isWarranty: 'isWarranty',
	isAssetDown: 'isAssetDown',
};

const hasActiveFilterValue = (value) => {
	if (value === undefined || value === null || value === '') {
		return false;
	}
	if (Array.isArray(value)) {
		return value.length > 0;
	}
	return true;
};

/**
 * Detects whether a filtered-for field on a work order meaningfully changed between the
 * version that populated the list (searchWorkOrder) and the freshly-fetched version
 * (freshWorkOrder). Only fields constrained by a currently-active filter are considered,
 * and a field that was undefined/null in the search version is ignored — a value going
 * from absent (lighter list projection) to present is NOT a meaningful change.
 *
 * Used to decide whether to drop the `preserve` param so a stale filtered list re-fetches
 * (e.g. another user moved a "Requested" work order to "In Progress" while it sat in the
 * current user's filtered, already-loaded list).
 */
export const didFilteredWorkOrderValueChange = (searchWorkOrder, freshWorkOrder, activeFilters) => {
	if (!searchWorkOrder || !freshWorkOrder || !activeFilters) {
		return false;
	}
	return Object.keys(activeFilters).some((filterKey) => {
		// `isStale` is not a plain field on the work order — the backend derives it as a
		// time-dependent condition (`completedAt IS NULL AND staleAt <= now()` plus
		// not-archived/not-cancelled), so there is no accessor path that reliably reflects
		// it and comparing search-vs-fresh would silently miss stale/fresh transitions.
		// When the Stale/Fresh filter (or the Stale Work Orders dashboard prefilter) is
		// active, force a re-fetch so a work order that flipped stale/fresh — or was
		// completed — while on detail doesn't linger in the filtered list.
		if (filterKey === 'isStale') {
			return hasActiveFilterValue(activeFilters[filterKey]);
		}
		const path = WO_FILTER_FIELD_ACCESSORS[filterKey];
		if (!path || !hasActiveFilterValue(activeFilters[filterKey])) {
			return false;
		}
		// nullSafeGetWithZero (unlike nullSafeGet) preserves falsy values like `false`/`0`,
		// so a boolean filter field that is genuinely `false` in search is distinguished
		// from one that is merely absent from the lighter list projection.
		const before = nullSafeGetWithZero(path, searchWorkOrder);
		if (before === undefined || before === null) {
			return false;
		}
		return !isEqual(before, nullSafeGetWithZero(path, freshWorkOrder));
	});
};
